import { IWorkFlowDto } from "@kuika/kuika-cl-model";
import { initialsFromName } from "../../../shared/utility/recently-modified";
import { normalizeForSearch } from "../../../shared/utility/search-normalize";
import {
  findWorkflowNameConflict,
  NamedItem,
  validateWorkflowName,
  workflowNameConflictMessage,
  type NameConflict
} from "../../../shared/utility/workflow-templates";

/**
 * Pure filter/sort helpers for the process rows behind the "All Processes" and
 * "Recently modified" tabs. Both tabs render the same table over the same fetched list.
 *
 * Which rows count as recent is deliberately NOT decided here, even though `IWorkFlowDto`
 * carries its own `updatedAt`: "recently" is the shared entity-modification feed's answer
 * (`recently-modified.ts`), so this landing means by it what the other seven do. The DTO's
 * timestamp still dates the "Last Updated" column and orders the default sort.
 */

export type ProcessStatusFilter = "active" | "inactive";

export type ProcessSortKey = "name-asc" | "name-desc" | "updated-desc" | "updated-asc";

export interface IProcessSortOption {
  value: ProcessSortKey;
  label: string;
}

export const PROCESS_SORT_OPTIONS: IProcessSortOption[] = [
  { value: "updated-desc", label: "Newest first" },
  { value: "updated-asc", label: "Oldest first" },
  { value: "name-asc", label: "Name (A–Z)" },
  { value: "name-desc", label: "Name (Z–A)" }
];

/**
 * The single seam for "when was this process last touched", as the row itself reports it:
 * the `_updatedDate` audit instant (`updatedAt`) when the process has been modified since
 * creation, else `createdAt`. The sort and the "Last Updated" column both go through here
 * so they stay in lockstep.
 */
export const getProcessTimestamp = (process: IWorkFlowDto): number | undefined => {
  const raw: Date | undefined = process.updatedAt ?? process.createdAt;
  if (!raw) return undefined;
  const parsed: number = new Date(raw).getTime();
  return Number.isNaN(parsed) ? undefined : parsed;
};

export const isProcessActive = (process: IWorkFlowDto): boolean => process.inactived !== true;

/**
 * Test-id slug for one process: its name with whitespace removed, lower-cased.
 *
 * The convention predates the gallery — the old Processes side panel inlined it for every row and
 * row-action id — and the E2E suite derives the same string from the name it is looking for. Both
 * the gallery table and the process switcher stamp it so a test can address a process by name
 * without reading text out of the DOM (the name cell is middle-truncated into head/tail spans).
 * `toLowerCase` is locale-independent in JS, so the id is the same under a Turkish locale.
 */
export const processTestIdSlug = (name: string): string => name.replace(/\s+/g, "").toLowerCase();

/**
 * "Ada Lovelace" → "AL", "Ada" → "AD" for the "Created by" avatar fallback. The rule is shared
 * with every other landing's writer avatar now that they all show one.
 */
export const getProcessCreatorInitials = (name: string | undefined): string => initialsFromName(name);

const matchesQuery = (process: IWorkFlowDto, query: string): boolean => {
  const trimmed: string = query.trim();
  if (!trimmed) return true;
  const needle: string = normalizeForSearch(trimmed);
  const name: string = process.displayName || process.name || "";
  return normalizeForSearch(name).includes(needle);
};

/**
 * Explicit "en" locale so the order is identical on a Turkish, German, or English
 * machine — a system-locale-dependent sort would make the list non-deterministic.
 */
const compareByName = (a: IWorkFlowDto, b: IWorkFlowDto): number =>
  (a.displayName || a.name || "").localeCompare(b.displayName || b.name || "", "en", { sensitivity: "base" });

/** Undated processes sort last in both directions rather than jumping to the top. */
const compareByTimestamp = (a: IWorkFlowDto, b: IWorkFlowDto, newestFirst: boolean): number => {
  const ta: number | undefined = getProcessTimestamp(a);
  const tb: number | undefined = getProcessTimestamp(b);
  if (ta === undefined && tb === undefined) return compareByName(a, b);
  if (ta === undefined) return 1;
  if (tb === undefined) return -1;
  if (ta === tb) return compareByName(a, b);
  return newestFirst ? tb - ta : ta - tb;
};

/**
 * Everything the rename check needs to see besides the process itself — the same
 * project-wide name space `createProcess` validates against, so rename and create
 * can never accept different names.
 */
export interface IProcessRenameContext {
  workflows?: NamedItem[];
  screens?: NamedItem[];
  datasources?: NamedItem[];
}

export type ProcessRenameOutcome =
  | { kind: "unchanged" }
  | { kind: "rejected"; message: string }
  | { kind: "accepted"; name: string };

/**
 * Decides what an inline rename should do with the typed value: nothing (same name),
 * reject with the shared naming-rule / collision message, or accept the trimmed name.
 * Same rules as the retired Processes-panel rename: the shared workflow-name pattern
 * plus case-insensitive uniqueness across workflows, screens, and datasources — with
 * the process itself excluded, so a case-only change ("my flow" → "My Flow") passes.
 */
export const resolveProcessRename = (
  process: IWorkFlowDto,
  rawName: string,
  context: IProcessRenameContext
): ProcessRenameOutcome => {
  const name: string = rawName.trim();
  if (name === (process.displayName || process.name)) return { kind: "unchanged" };

  const invalid: string | null = validateWorkflowName(name);
  if (invalid) return { kind: "rejected", message: invalid };

  const conflict: NameConflict | null = findWorkflowNameConflict({
    name,
    workflows: context.workflows,
    screens: context.screens,
    datasources: context.datasources,
    excludeWorkflowId: process.id
  });
  if (conflict) return { kind: "rejected", message: workflowNameConflictMessage(conflict) };

  return { kind: "accepted", name };
};

export const filterProcesses = (
  processes: readonly IWorkFlowDto[],
  query: string,
  status: ProcessStatusFilter | null,
  sort: ProcessSortKey | null
): IWorkFlowDto[] => {
  const filtered: IWorkFlowDto[] = processes.filter((process: IWorkFlowDto): boolean => {
    if (!matchesQuery(process, query)) return false;
    if (status === "active") return isProcessActive(process);
    if (status === "inactive") return !isProcessActive(process);
    return true;
  });

  // The default (no explicit sort) is newest-activity first — the fetch arrives
  // oldest-first, and a "your processes" table reading oldest-first is the wrong default.
  // An explicit Sort-by choice overrides it.
  const effectiveSort: ProcessSortKey = sort ?? "updated-desc";
  return [...filtered].sort((a: IWorkFlowDto, b: IWorkFlowDto): number => {
    if (effectiveSort === "name-asc") return compareByName(a, b);
    if (effectiveSort === "name-desc") return compareByName(b, a);
    if (effectiveSort === "updated-asc") return compareByTimestamp(a, b, false);
    return compareByTimestamp(a, b, true);
  });
};
